import api from '@/services/api';
import type { DashboardItem, ThirdPartyStatus } from '../types/dashboard.types';
import { isThirdPartyItem } from './thirdPartyAgreements';

interface DashboardItemsListResponse {
    success: boolean;
    data: DashboardItem[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
        third_party?: unknown;
    };
}
interface DashboardResponse {
    items: DashboardItem[]; // or dashboardItems/searchItems - normalize here
    meta: {
        current_page: number;
        last_page: number;
        total: number;
        per_page: number;
        from: number;
        to: number;
    };
    thirdParty: ThirdPartyStatus;
}

const THIRD_PARTY_STATUSES: readonly ThirdPartyStatus[] = ['included', 'none', 'unavailable', 'not_applicable'];

const readThirdPartyStatus = (value: unknown): ThirdPartyStatus =>
    THIRD_PARTY_STATUSES.find((status) => status === value) ?? 'not_applicable';

const keepValidRows = (rows: DashboardItem[]): { items: DashboardItem[]; droppedAny: boolean } => {
    const items = rows.filter((row) => row.source !== 'third_party' || isThirdPartyItem(row));
    return { items, droppedAny: items.length !== rows.length };
};

export type QuoteConversionState =
    | { state: 'resolved'; agreementId: string }
    | { state: 'pending' } // the row is visible and agreement_id is still null
    | { state: 'unknown' }; // no row I can see: out of entitlement, or never projected

export const fetchQuoteConversionState = async (quoteId: string): Promise<QuoteConversionState> => {
    const raw = (
        await api.post(
            '/dashboard/search/',
            { record_id: quoteId, per_page: 5 },
            { suppressGlobalForbiddenToast: true }
        )
    ).data;
    const rows = (raw.data ?? []) as DashboardItem[];

    if (rows.length === 0) return { state: 'unknown' };

    // ANY row, not rows[0]: a quote's lineage can hold two rows tied on `created_at_ts`,
    // so the returned order is planner order, not an answer.
    const claimed = rows.find((r) => r.agreement_id);

    return claimed
        ? { state: 'resolved', agreementId: claimed.agreement_id as string }
        : { state: 'pending' };
};

export const fetchDashboardItems = async (params: Record<string, unknown>): Promise<DashboardResponse> => {
    const response = await api.post<DashboardItemsListResponse>('/dashboard/search/', { ...params });

    if (!response.data.success) {
        throw new Error('Failed to fetch dashboard items');
    }

    const { third_party: rawThirdParty, ...meta } = response.data.meta;
    const { items, droppedAny } = keepValidRows(response.data.data);

    return {
        items,
        meta: {
            ...meta,
            from: meta.from ?? 0,
            to: meta.to ?? 0,
        },
        thirdParty: droppedAny ? 'unavailable' : readThirdPartyStatus(rawThirdParty),
    };
};
